import { ReactNode } from 'react'
import { InputGroup } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import {
  ControllerProps,
  Controller,
  FieldPath,
  FieldValues,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form'
import { ThemeInterface } from 'themes/types'
import ColorPickerRhf from 'shared/components/color-picker-rhf'
import { themeColorOptions } from '../consts/theme-color-loco'

type ColorFieldInterface<TValues extends FieldValues, TName extends FieldPath<TValues>> = Omit<
  ControllerProps<TValues, TName>,
  'render'
> & {
  setValue: UseFormSetValue<TValues>
  afterChange?: (value: PathValue<TValues, TName>) => void
  error?: string
}

export default function ThemeEditorColorField<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
>({ name, control, setValue, afterChange, error }: ColorFieldInterface<TValues, TName>): ReactNode {
  const { t } = useTranslation()

  const colorName = themeColorOptions[name as keyof ThemeInterface]

  return (
    <div className="grid grid-cols-[2.5rem_1fr] grid-rows-[2rem_1fr] items-center gap-x-2">
      <span className="col-span-2 self-end">{t(colorName)}</span>
      <div className="col-start-1 row-start-2">
        <ColorPickerRhf
          className="size-8"
          control={control}
          name={name}
          afterChange={afterChange}
          setValue={setValue}
        />
      </div>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <InputGroup.Root size="sm" className="col-start-2 row-start-2 min-w-0">
            <InputGroup.Input
              value={field.value ?? ''}
              onChange={e => {
                const typedValue = e.target.value as PathValue<TValues, TName>
                setValue(name, typedValue)
                afterChange?.(typedValue)
              }}
            />
          </InputGroup.Root>
        )}
      />
      {error && <p className="col-span-2 text-sm text-text-error">{error}</p>}
    </div>
  )
}
